import React, { useCallback, useEffect, useState } from "react";
import { useI18n } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";

function PaymentRow({ item, lang, reload }) {
  const text = (it, en) => lang === "it" ? it : en;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fmt = (date) => new Date(date).toLocaleString(lang === "it" ? "it-IT" : "en-GB");
  const save = async (mode) => {
    setBusy(true); setError("");
    try {
      await api.patch(`/admin/payment-retention/${item.id}`, {
        mode, reason, expected_updated_at: item.updated_at || null,
        expected_changed_at: item.retention.changed_at || null, expected_status: item.status || null,
      });
      setReason(""); await reload();
    } catch (e) {
      setError(e.response?.status === 409
        ? text("Il record è cambiato. Aggiorna l’elenco e riprova.", "The record changed. Refresh the list and try again.")
        : text("Aggiornamento non riuscito. Riprova.", "Update failed. Try again."));
    } finally { setBusy(false); }
  };
  const paid = item.payment_status === "paid" || item.status === "completed";
  return <article className="nvl-card p-4 space-y-3" data-testid="payment-retention-row">
    <p className="font-medium">{item.provider === "stripe" ? "Stripe" : item.provider === "paypal" ? "PayPal" : text("Gestore da verificare", "Provider to verify")} · {paid ? text("Pagamento completato", "Completed payment") : item.status === "expired" ? text("Tentativo scaduto", "Expired attempt") : text("Stato da verificare", "Status to verify")}</p>
    <p className="text-xs break-all">{item.session_id}</p>
    <p className="text-sm" role="status">{item.retention.hold
      ? text("Pulizia sospesa", "Cleanup on hold")
      : item.retention.expires_at ? text("Rimozione possibile dal: ", "Eligible for removal from: ") + fmt(item.retention.expires_at)
      : text("Nessuna rimozione programmata", "No removal scheduled")}</p>
    {item.retention.reason && <p className="text-sm">{text("Ultima motivazione: ", "Latest reason: ")}{item.retention.reason}</p>}
    {item.retention.review_at && <p className="text-sm">{text("Riesame manuale entro: ", "Manual review by: ")}{fmt(item.retention.review_at)}</p>}
    <label className="block text-sm">{text("Motivo della decisione (senza dati sensibili)", "Reason for this decision (no sensitive data)")}
      <input className="nvl-input mt-2" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
    </label>
    <div className="flex flex-wrap gap-2">
      <button type="button" disabled={busy || !reason.trim()} onClick={() => save("hold")} className="nvl-btn nvl-btn-secondary">{item.retention.hold ? text("Conferma sospensione e nuovo riesame", "Confirm hold and new review") : text("Sospendi la pulizia", "Hold cleanup")}</button>
      {item.retention.hold && <button type="button" disabled={busy || !reason.trim()} onClick={() => save("release")} className="nvl-btn nvl-btn-secondary">{text("Controversia chiusa: rimuovi sospensione", "Dispute closed: release hold")}</button>}
    </div>
    {item.retention.hold && <p className="text-xs">{text("Rimuovere la sospensione non fa ripartire i 90 giorni: se già trascorsi, il record potrà essere eliminato dopo una nuova verifica con Stripe.", "Releasing a hold does not restart the 90 days: if overdue, the record may be removed after a new Stripe verification.")}</p>}
    {error && <p role="alert" className="text-sm">{error}</p>}
  </article>;
}

export default function PaymentRetention() {
  const { lang } = useI18n();
  const text = (it, en) => lang === "it" ? it : en;
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { const r = await api.get(`/admin/payment-retention?page=${page}`); setData(r.data); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, [page]);
  useEffect(() => { load(); }, [load]);
  return <section className="mt-8 space-y-4" data-testid="payment-retention-panel">
    <h2 className="text-2xl">{text("Conservazione dei tentativi di pagamento", "Payment attempt retention")}</h2>
    <p className="text-sm">{text("Questi comandi riguardano soltanto la pulizia dei dati locali: non modificano pagamenti o abbonamenti. Le sospensioni richiedono un riesame manuale dopo 30 giorni e non scadono automaticamente.", "These controls only manage cleanup of local records: payments and subscriptions are unchanged. Holds need a manual review after 30 days and do not expire automatically.")}</p>
    <button type="button" onClick={load} disabled={loading} className="nvl-btn nvl-btn-secondary">{text("Aggiorna elenco", "Refresh list")}</button>
    {loading ? <p>{text("Caricamento…", "Loading…")}</p> : error ? <p role="alert">{text("Impossibile caricare l’elenco. Riprova.", "Unable to load the list. Try again.")}</p> : <>
      {data?.items.length === 0 && <p>{text("Nessun record presente.", "No records found.")}</p>}
      {data?.items.map((item) => <PaymentRow key={item.id} item={item} lang={lang} reload={load} />)}
      <div className="flex gap-3">
        <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>{text("Precedenti", "Previous")}</button>
        <span>{text("Pagina ", "Page ")}{page}</span>
        <button type="button" disabled={!data?.has_more} onClick={() => setPage(page + 1)}>{text("Successivi", "Next")}</button>
      </div>
    </>}
  </section>;
}
